/**
 * Phase 2 — Structured Neuro Report Templates.
 *
 * Each template lists the SECTIONS a doctor fills out for a given condition.
 * Fields use a stable `key` so backend storage stays consistent even if labels change.
 * AI-assist is enabled per field via the `aiKey` matching FIELD_PROMPTS in
 * `/app/backend/routes/ai_suggest.py`.
 */
import { Brain, Activity, Zap, Bone, AlertTriangle, BrainCircuit, Footprints, ArrowDown, Skull, FileText } from "lucide-react";

export const TEMPLATES = [
  {
    id: "brain_tumor",
    label: "Brain Tumor",
    icon: Brain,
    color: "from-rose-500 to-rose-700",
    sections: [
      { key: "diagnosis",              label: "Diagnosis",              aiKey: "diagnosis",            type: "text", placeholder: "e.g., Right frontal high-grade glioma" },
      { key: "grade",                  label: "Grade / Stage",          aiKey: "grade",                type: "text", placeholder: "e.g., WHO Grade IV" },
      { key: "imaging_correlation",    label: "Imaging correlation",    aiKey: "diagnosis",            type: "textarea", placeholder: "Findings on MRI/CT and how they correlate with the clinical picture" },
      { key: "surgical_recommendation",label: "Surgical recommendation",aiKey: "surgical_recommendation", type: "textarea", placeholder: "Procedure recommended + rationale" },
      { key: "risks",                  label: "Surgical risks",         aiKey: "risks",                type: "textarea", placeholder: "Specific risks for this patient" },
      { key: "alternatives",           label: "Alternative treatments", aiKey: "alternatives",         type: "textarea", placeholder: "Radiation, chemo, watchful waiting" },
      { key: "prognosis",              label: "Prognosis",              aiKey: "prognosis",            type: "textarea", placeholder: "Expected outcome with / without intervention" },
      { key: "follow_up",              label: "Follow-up plan",         aiKey: "follow_up",            type: "text", placeholder: "Imaging interval, clinic review" },
    ],
  },
  {
    id: "spine",
    label: "Spine / Back Pain",
    icon: Bone,
    color: "from-amber-500 to-orange-700",
    sections: [
      { key: "imaging_findings",       label: "Key imaging findings",   aiKey: "diagnosis",            type: "textarea", placeholder: "L4-L5 disc herniation impinging right L5 root..." },
      { key: "symptom_correlation",    label: "Symptom correlation",    aiKey: "diagnosis",            type: "textarea", placeholder: "Findings match radicular pattern" },
      { key: "diagnosis",              label: "Working diagnosis",      aiKey: "diagnosis",            type: "text" },
      { key: "surgical_recommendation",label: "Need for surgery",       aiKey: "surgical_recommendation", type: "textarea" },
      { key: "alternatives",           label: "Non-surgical options",   aiKey: "alternatives",         type: "textarea", placeholder: "PT, epidural steroid, NSAIDs" },
      { key: "medications",            label: "Medications",            aiKey: "medications",          type: "textarea" },
      { key: "lifestyle",              label: "Lifestyle / physio",     aiKey: "lifestyle",            type: "textarea" },
      { key: "red_flags",              label: "Red-flag symptoms",      aiKey: "red_flags",            type: "textarea", placeholder: "Bowel/bladder changes, saddle anaesthesia..." },
      { key: "follow_up",              label: "Follow-up",              aiKey: "follow_up",            type: "text" },
    ],
  },
  {
    id: "stroke",
    label: "Stroke",
    icon: Activity,
    color: "from-rose-600 to-red-800",
    sections: [
      { key: "stroke_type",            label: "Stroke type",            aiKey: "diagnosis",            type: "text", placeholder: "Ischaemic / haemorrhagic / TIA" },
      { key: "etiology",               label: "Likely etiology",        aiKey: "diagnosis",            type: "text", placeholder: "Cardio-embolic, large-artery, lacunar..." },
      { key: "imaging_findings",       label: "Imaging findings",       aiKey: "diagnosis",            type: "textarea" },
      { key: "acute_management",       label: "Acute management",       aiKey: "surgical_recommendation", type: "textarea", placeholder: "Thrombolysis, thrombectomy, BP control" },
      { key: "secondary_prevention",   label: "Secondary prevention",   aiKey: "medications",          type: "textarea", placeholder: "Antiplatelets, statins, BP/DM control" },
      { key: "rehabilitation",         label: "Rehabilitation plan",    aiKey: "lifestyle",            type: "textarea" },
      { key: "follow_up",              label: "Follow-up",              aiKey: "follow_up",            type: "text" },
    ],
  },
  {
    id: "epilepsy",
    label: "Epilepsy",
    icon: Zap,
    color: "from-purple-600 to-indigo-700",
    sections: [
      { key: "seizure_type",           label: "Seizure type",           aiKey: "diagnosis",            type: "text", placeholder: "Focal, generalised, absence..." },
      { key: "etiology",               label: "Likely etiology",        aiKey: "diagnosis",            type: "text" },
      { key: "imaging_eeg",            label: "Imaging / EEG findings", aiKey: "diagnosis",            type: "textarea" },
      { key: "medications",            label: "Anti-epileptic plan",    aiKey: "medications",          type: "textarea" },
      { key: "surgical_evaluation",    label: "Surgical workup needed?", aiKey: "surgical_recommendation", type: "textarea", placeholder: "Refractory? VEEG, PET, surgical candidacy" },
      { key: "lifestyle",              label: "Lifestyle precautions",  aiKey: "lifestyle",            type: "textarea", placeholder: "Driving, swimming, sleep hygiene" },
      { key: "red_flags",              label: "Red flags",              aiKey: "red_flags",            type: "textarea" },
      { key: "follow_up",              label: "Follow-up",              aiKey: "follow_up",            type: "text" },
    ],
  },
  {
    id: "headache",
    label: "Headache / Migraine",
    icon: Brain,
    color: "from-pink-500 to-rose-700",
    sections: [
      { key: "diagnosis",              label: "Headache type",          aiKey: "diagnosis",            type: "text", placeholder: "Migraine without aura / tension / cluster" },
      { key: "imaging_correlation",    label: "Imaging review",         aiKey: "diagnosis",            type: "textarea", placeholder: "Need for MRI / CT, findings if any" },
      { key: "acute_management",       label: "Acute treatment",        aiKey: "medications",          type: "textarea", placeholder: "Triptans, NSAIDs" },
      { key: "preventive",             label: "Preventive treatment",   aiKey: "medications",          type: "textarea", placeholder: "Topiramate, propranolol, CGRP inhibitor" },
      { key: "lifestyle",              label: "Lifestyle / triggers",   aiKey: "lifestyle",            type: "textarea" },
      { key: "red_flags",              label: "Red flags",              aiKey: "red_flags",            type: "textarea" },
      { key: "follow_up",              label: "Follow-up",              aiKey: "follow_up",            type: "text" },
    ],
  },
  {
    id: "ms",
    label: "Multiple Sclerosis",
    icon: BrainCircuit,
    color: "from-violet-600 to-purple-800",
    sections: [
      { key: "diagnosis",              label: "MS confirmation",        aiKey: "diagnosis",            type: "text", placeholder: "MS phenotype: RRMS / SPMS / PPMS / CIS" },
      { key: "imaging_findings",       label: "MRI lesion load",        aiKey: "diagnosis",            type: "textarea" },
      { key: "dmt_recommendation",     label: "DMT recommendation",     aiKey: "medications",          type: "textarea", placeholder: "Ocrelizumab, natalizumab, dimethyl fumarate..." },
      { key: "symptomatic_treatment",  label: "Symptom management",     aiKey: "medications",          type: "textarea" },
      { key: "monitoring",             label: "Monitoring plan",        aiKey: "follow_up",            type: "textarea", placeholder: "MRI interval, EDSS, labs" },
      { key: "lifestyle",              label: "Lifestyle / rehab",      aiKey: "lifestyle",            type: "textarea" },
    ],
  },
  {
    id: "dementia",
    label: "Dementia / Alzheimer's",
    icon: BrainCircuit,
    color: "from-amber-600 to-orange-800",
    sections: [
      { key: "diagnosis",              label: "Dementia subtype",       aiKey: "diagnosis",            type: "text", placeholder: "Alzheimer's, Lewy body, vascular, FTD" },
      { key: "cognitive_assessment",   label: "Cognitive assessment",   aiKey: "diagnosis",            type: "textarea", placeholder: "MMSE, MoCA scores; domains affected" },
      { key: "reversible_causes",      label: "Reversible causes ruled out", aiKey: "diagnosis",       type: "textarea", placeholder: "B12, thyroid, depression, sleep apnoea" },
      { key: "medications",            label: "Pharmacological plan",   aiKey: "medications",          type: "textarea", placeholder: "Donepezil, memantine, lecanemab" },
      { key: "non_pharm",              label: "Non-pharm interventions",aiKey: "lifestyle",            type: "textarea" },
      { key: "caregiver_guidance",     label: "Caregiver guidance",     aiKey: "lifestyle",            type: "textarea" },
      { key: "follow_up",              label: "Follow-up",              aiKey: "follow_up",            type: "text" },
    ],
  },
  {
    id: "sciatica",
    label: "Sciatica",
    icon: ArrowDown,
    color: "from-orange-500 to-red-700",
    sections: [
      { key: "imaging_findings",       label: "Imaging findings",       aiKey: "diagnosis",            type: "textarea" },
      { key: "diagnosis",              label: "Working diagnosis",      aiKey: "diagnosis",            type: "text" },
      { key: "conservative_trial",     label: "Conservative trial",     aiKey: "alternatives",         type: "textarea", placeholder: "6-week PT + NSAIDs + neuropathic agents" },
      { key: "surgical_recommendation",label: "When to consider surgery",aiKey: "surgical_recommendation", type: "textarea" },
      { key: "medications",            label: "Medications",            aiKey: "medications",          type: "textarea" },
      { key: "red_flags",              label: "Red flags",              aiKey: "red_flags",            type: "textarea", placeholder: "Cauda equina — surgical emergency" },
      { key: "follow_up",              label: "Follow-up",              aiKey: "follow_up",            type: "text" },
    ],
  },
  {
    id: "general",
    label: "General / Free-form",
    icon: FileText,
    color: "from-slate-500 to-slate-700",
    sections: [
      { key: "diagnosis",              label: "Diagnosis",              aiKey: "diagnosis",            type: "text" },
      { key: "findings_summary",       label: "Key findings",           aiKey: "diagnosis",            type: "textarea" },
      { key: "plan",                   label: "Plan / recommendations", aiKey: "surgical_recommendation", type: "textarea" },
      { key: "medications",            label: "Medications",            aiKey: "medications",          type: "textarea" },
      { key: "follow_up",              label: "Follow-up",              aiKey: "follow_up",            type: "text" },
    ],
  },
];

export const TEMPLATE_BY_ID = Object.fromEntries(TEMPLATES.map((t) => [t.id, t]));

// Map symptom tags / condition slugs from intake → suggested template id.
export const SLUG_TO_TEMPLATE = {
  "brain-tumor": "brain_tumor",
  "stroke": "stroke",
  "epilepsy": "epilepsy",
  "spine-back-pain": "spine",
  "headache-migraine": "headache",
  "parkinsons": "general",
  "pediatric-neurology": "general",
  "multiple-sclerosis": "ms",
  "dementia-alzheimers": "dementia",
  "sciatica": "sciatica",
  "peripheral-neuropathy": "general",
  "head-injury-concussion": "general",
  "cervical-spondylosis": "spine",
};

export function pickTemplateFor(consultation) {
  const tags = (consultation?.symptom_tags || []).map((t) => t.toLowerCase());
  if (tags.includes("brain tumor") || tags.includes("brain")) return "brain_tumor";
  if (tags.includes("stroke") || tags.includes("weakness")) return "stroke";
  if (tags.includes("seizure") || tags.includes("epilepsy")) return "epilepsy";
  if (tags.includes("back pain") || tags.includes("spine") || tags.includes("numbness")) return "spine";
  if (tags.includes("headache") || tags.includes("migraine")) return "headache";
  if (tags.includes("memory loss") || tags.includes("dementia")) return "dementia";
  return "general";
}
